import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { MailQuestion, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * /verify-email/resend — request a fresh verification link.
 * Always shows the same success message (anti-enumeration).
 */
export default function ResendVerificationPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t("resend.err_email_required"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      // Backend returns 200 even on unknown emails — only network errors land here.
      toast.error(t("resend.err_network"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-zinc-950 via-black to-zinc-950 p-6" data-testid="resend-verification-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <Logo showText={false} />
          </Link>
        </div>
        <div className="border border-white/10 bg-zinc-900/40 rounded-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-orange-500/10 border-2 border-orange-500/30 rounded-full flex items-center justify-center mb-4">
              <MailQuestion className="text-orange-400" size={32} />
            </div>
            <h1 className="font-outfit font-black text-white text-2xl mb-2">
              {t("resend.title")}
            </h1>
            <p className="text-zinc-400 text-sm">
              {t("resend.subtitle")}
            </p>
          </div>

          {sent ? (
            <div className="text-center" data-testid="resend-success">
              <p className="text-emerald-400 text-sm mb-4">
                {t("resend.success_body")}
              </p>
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => navigate("/login")}
              >
                {t("resend.go_to_login")}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="resend-email" className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                  {t("resend.email_label")}
                </Label>
                <Input
                  id="resend-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-zinc-950/50 border-white/10 text-white mt-2 h-12"
                  data-testid="resend-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12"
                data-testid="resend-submit"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-2" /> {t("resend.submit_loading")}
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" /> {t("resend.submit_btn")}
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
